import type { CompanyBlockData } from "@/types";

interface CompanyCardProps {
  data: CompanyBlockData;
}

function formatFinancial(value: number): string {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)} Mdr`;
  if (value >= 1_000_000) return `${Math.round(value / 1_000_000)} Mkr`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}k`;
  return value.toLocaleString();
}

/** Entity type colors — matches map entity colors from styles.css */
const SEGMENT_COLORS = {
  projects: "#2A7A6E",
  permits: "#3A8F95",
  tenders: "#245E6E",
} as const;

export function CompanyCard({ data }: CompanyCardProps) {
  // Build metrics (only non-null values)
  const metrics: { value: string; label: string }[] = [];
  if (data.revenue) metrics.push({ value: formatFinancial(data.revenue), label: "Revenue" });
  if (data.number_of_employees) metrics.push({ value: data.number_of_employees.toLocaleString(), label: "Employees" });
  if (data.equity) metrics.push({ value: formatFinancial(data.equity), label: "Equity" });
  if (data.ebitda) metrics.push({ value: formatFinancial(data.ebitda), label: "EBITDA" });

  // Meta line: org number · city · founded year
  const metaParts: string[] = [];
  if (data.organization_number) metaParts.push(data.organization_number);
  if (data.city) metaParts.push(data.city);
  if (data.founding_year) metaParts.push(`Founded ${data.founding_year}`);
  const metaLine = metaParts.join(" · ");

  return (
    <div className="cx-co animate-cx-message-in">
      {/* Header */}
      <div className="cx-co-header">
        <div>
          <div className="cx-co-name">{data.display_name}</div>
          {metaLine && <div className="cx-co-meta">{metaLine}</div>}
        </div>
        {data.is_parent_company && (
          <span className="cx-co-badge">Parent company</span>
        )}
      </div>

      {/* Metrics */}
      {metrics.length > 0 && (
        <div className="cx-co-metrics">
          {metrics.map((m, i) => (
            <div key={i} className="cx-co-metric">
              <span className={`cx-co-metric-value${i === 0 ? " cx-co-metric-value--accent" : ""}`}>{m.value}</span>
              <span className="cx-co-metric-label">{m.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Corporate structure */}
      {data.structure && data.structure.length > 0 && (
        <div className="cx-co-section">
          <div className="cx-co-section-title">Corporate structure</div>
          <div className="cx-co-structure">
            {data.structure.map((node, i) => (
              <div key={i} className={`cx-co-node cx-co-node--${node.relationship}`}>
                <span className="cx-co-node-name">{node.name}</span>
                {node.detail && <span className="cx-co-node-detail">{node.detail}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Activity footprint */}
      {data.footprint && data.footprint.length > 0 && (
        <div className="cx-co-section">
          <div className="cx-co-section-title">
            Activity{data.footprint_context ? ` in ${data.footprint_context}` : ""}
          </div>
          <div className="cx-co-footprint">
            {data.footprint.map((row, i) => {
              const maxTotal = data.footprint![0]!.total;
              return (
                <div key={i} className="cx-co-fp-row">
                  <span className="cx-co-fp-role">{row.role}</span>
                  <div className="cx-co-fp-bar-wrap">
                    <div className="cx-co-fp-segments" style={{ width: `${(row.total / maxTotal) * 100}%` }}>
                      {(["projects", "permits", "tenders"] as const).map((type) => {
                        const count = row[type];
                        if (!count) return null;
                        return (
                          <div
                            key={type}
                            className="cx-co-fp-seg"
                            style={{ flex: count, background: SEGMENT_COLORS[type] }}
                            title={`${count} ${type}`}
                          >
                            {count >= 3 && <span className="cx-co-fp-seg-label">{count}</span>}
                          </div>
                        );
                      })}
                    </div>
                    <span className="cx-co-fp-total">{row.total}</span>
                  </div>
                </div>
              );
            })}
            <div className="cx-co-fp-legend">
              <span><span className="cx-co-fp-dot" style={{ background: SEGMENT_COLORS.projects }} />Projects</span>
              <span><span className="cx-co-fp-dot" style={{ background: SEGMENT_COLORS.permits }} />Permits</span>
              <span><span className="cx-co-fp-dot" style={{ background: SEGMENT_COLORS.tenders }} />Tenders</span>
            </div>
          </div>
        </div>
      )}

      {/* Contacts */}
      {data.contacts && data.contacts.length > 0 && (
        <div className="cx-co-section">
          <div className="cx-co-section-title">Contacts</div>
          {data.contacts.map((c, i) => (
            <div key={i} className="cx-co-contact">
              <div>
                <span>{c.name}</span>
                {c.role && <span className="cx-co-contact-role">{c.role}</span>}
              </div>
              {c.phone && <span className="cx-co-contact-phone">{c.phone}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
